/**
 * NFL API client — isolated. Hits /nfl/* endpoints. Every route now exists on the
 * backend, so a failed fetch returns an HONEST EMPTY shape (never fabricated
 * "sample" rows) flagged `_sample:true`, and each surface shows its own empty state.
 * No invented numbers ever render as if live.
 */
const BASE = import.meta.env.VITE_API_URL || 'https://linebrkr-deploy-production.up.railway.app'

// The app sits behind a global access-code gate. Reuse the same code the MLB
// client stores (localStorage 'brkr_access') so /nfl/* reads pass the gate when
// the owner/beta user is signed in. No code set (dev) → header omitted.
const _hdrs = () => {
  try {
    const c = localStorage.getItem('brkr_access')
    return c ? { 'x-access-code': c } : {}
  } catch (_) { return {} }
}

// Anonymous usage events (see track.js) — fire-and-forget; keepalive so a batch sent while the
// page is closing still lands. Errors are swallowed: tracking must never affect the app.
export const postNFLEvents = (body) => {
  try {
    return fetch(`${BASE}/nfl/event`, { method: 'POST', keepalive: true,
      headers: { 'content-type': 'application/json', ..._hdrs() }, body: JSON.stringify(body) }).catch(() => {})
  } catch (_) { return Promise.resolve() }
}

async function tryGet(path, empty) {
  // Any non-ok response or network drop returns the caller's EMPTY structural shape
  // (keys present, arrays empty) flagged so the UI can show "live data unavailable".
  // We never fabricate rows — an empty board is honest; invented numbers are not.
  try {
    const r = await fetch(`${BASE}${path}`, { headers: _hdrs() })
    if (r.ok) return await r.json()
    return { ...(empty || {}), _sample: true, _status: r.status }
  } catch (e) {
    return { ...(empty || {}), _sample: true }
  }
}

// Core 6 prop markets (see docs/NFL_PLAN.md). Order = the selector order on the
// Rankings + Home boards: touchdowns lead, then yardage, then the rest.
export const NFL_MARKETS = [
  { key: 'anytime_td', label: 'Anytime TD' },
  { key: 'rec_yards',  label: 'Rec Yds' },
  { key: 'rush_yards', label: 'Rush Yds' },
  { key: 'pass_yards', label: 'Pass Yds' },
  { key: 'pass_tds',   label: 'Pass TDs' },
  { key: 'receptions', label: 'Receptions' },
]

export const getNFLLeaderboard = (market = 'receptions', season, week, limit = 250) =>
  tryGet(`/nfl/projections/leaderboard?market=${market}&season=${season}&week=${week}&limit=${limit}`,
         { market, rows: [], carryover: false, source_season: season, injuries_loaded: true })

export const getNFLSlate = (season, week) =>
  tryGet(`/nfl/slate?season=${season}&week=${week}`, { games: [] })

// Public lock-screen teaser (ungated) — one top play + this week's counts.
export const getNFLPreview = (season) =>
  tryGet(`/nfl/preview?season=${season}`, { top: null, plays: 0, games: 0, top_rated_hit: null })

export const getNFLGame = (gameId, season, week) =>
  tryGet(`/nfl/game?game_id=${encodeURIComponent(gameId)}&season=${season}&week=${week}`, {})

// "Bet on Kalshi" handoff link — hands off to Kalshi to place the bet THERE (research + handoff;
// no money/credentials touch the app). Returns { enabled, url, matchup, exact_market }.
export const getKalshiLink = (gameId, season, week, team) =>
  tryGet(`/nfl/kalshi-link?game_id=${encodeURIComponent(gameId || '')}&team=${encodeURIComponent(team || '')}&season=${season}&week=${week}`,
         { enabled: false })

// Game Plan — the opponent's defensive soft spots (incl. slot vs outside) + the specific players
// who exploit them, as proposed props with the matchup rank as the "why".
export const getNFLGamePlan = (gameId, season, week) =>
  tryGet(`/nfl/game-plan?game_id=${encodeURIComponent(gameId)}&season=${season}&week=${week}`,
         { plans: { away: null, home: null } })

// "The Read" — AI-written 60-second preview, grounded in the game-plan data. { read: null }
// when the AI key isn't configured (the client then just shows the structured brief).
export const getNFLGameRead = (gameId, season, week) =>
  tryGet(`/nfl/game-read?game_id=${encodeURIComponent(gameId)}&season=${season}&week=${week}`, { read: null })

// LOCKS — most likely to CASH at the offered line (ranked by likelihood, not edge).
export const getNFLLocks = (season, week) =>
  tryGet(`/nfl/locks?season=${season}&week=${week}`, { locks: [] })

// TD MATCHUPS BY ROLE — TDs each defense allows to WR1/WR2/WR3, TE1/TE2, RB1/RB2, QB rush vs
// league (shrunk for small samples) + this week's players with a role-adjusted TD/g.
// Value touchdown plays — 25-45% to score + a soft role matchup, priced vs the book, with the
// list's own 'in testing' record.
export const getNFLValueTds = (season, week) =>
  tryGet(`/nfl/value-tds?season=${season}&week=${week}`, { rows: [], record: {} })

export const getNFLTdRoles = (season, week, team = '') =>
  tryGet(`/nfl/td-role-matchups?season=${season}&week=${week}${team ? `&team=${team}` : ''}`,
    { defenses: [], players: [] })

// FADES — favorable UNDERS (a −EV over = a +EV under); two-way markets only.
// CHECK MY SLIP — POST the slip's TEXT (pasted, or read from a screenshot on the user's phone);
// the image itself never leaves the device. Returns graded legs + whole-ticket odds.
export async function checkNFLSlip(text, season, week) {
  try {
    const r = await fetch(`${BASE}/nfl/slip-check`, { method: 'POST',
      headers: { 'content-type': 'application/json', ..._hdrs() }, body: JSON.stringify({ text, season, week }) })
    if (r.ok) return await r.json()
    return { legs: [], unmatched: [], _error: r.status }
  } catch (e) { return { legs: [], unmatched: [], _error: 'network' } }
}

export const getNFLFades = (season, week, limit) =>
  tryGet(`/nfl/fades?season=${season}&week=${week}${limit ? `&limit=${limit}` : ''}`, { fades: [] })

// Curated home-page angle signals (shootout / air raid / ground control / stack),
// keyed off game environment AND defense-vs-position softness. Server-side so the
// logic is shared and notification-ready.
export const getNFLSignals = (season, week) =>
  tryGet(`/nfl/signals?season=${season}&week=${week}`,
         { shootout: null, air_raid: [], ground_control: [], stack: null, td_storm: [], td_outlook: null })

// Calibration vs the sharp market we ingest — our game lines & props vs the book,
// with MAE + signed bias. The honest accuracy readout.
export const getNFLMarketAudit = (season, week) =>
  tryGet(`/nfl/market-audit?season=${season}&week=${week}`,
         { game_lines: { spread: {}, total: {}, priced_games: 0, worst: [] }, props: {} })

// Line-shopping — every US book's price for one player/market, best over first.
export const getNFLBookOdds = (gsisId, propType, season, week) =>
  tryGet(`/nfl/book-odds?gsis_id=${encodeURIComponent(gsisId)}&prop_type=${encodeURIComponent(propType)}&season=${season}&week=${week}`,
         { books: [], best: null, consensus: null })

// Full over/under ladder for a player·market (standard + alternate rungs), each with the
// best over price, model prob and edge, plus the best-value rung. Alt rungs appear only
// when NFL_ALT_LINES is enabled server-side.
export const getNFLBookOddsLadder = (gsisId, propType, season, week) =>
  tryGet(`/nfl/book-odds-ladder?gsis_id=${encodeURIComponent(gsisId)}&prop_type=${encodeURIComponent(propType)}&season=${season}&week=${week}`,
         { rungs: [], best_value: null, alt_enabled: false })

export const getNFLTdLab = (season, week) =>
  tryGet(`/nfl/td-lab?season=${season}&week=${week}`, { rows: [] })

// Today's Best — the tight shortlist of top RECOMMENDED plays across every market (post-
// calibration gating), one per player, ranked by conviction with a value tilt.
export const getNFLToday = (season, week, n = 5) =>
  tryGet(`/nfl/today?season=${season}&week=${week}&n=${n}`, { plays: [] })

// Ladder Spotter — yardage players set up to climb: model P(>=) at each rung, ranked by juice.
export const getNFLLadder = (season, week, market = '', limit = 25, long = false) =>
  tryGet(`/nfl/ladder?season=${season}&week=${week}${market ? `&market=${encodeURIComponent(market)}` : ''}&limit=${limit}${long ? '&long=true' : ''}`,
         { players: [], alt_enabled: false })

// Per-market calibration status for the board's honesty badges (calibrated / hot / sharp /
// building) — how well each market's shown probabilities have matched reality this season.
export const getNFLMarketCalibration = (season) =>
  tryGet(`/nfl/market-calibration?season=${season}`, { markets: {} })

// Prime Spots — players whose market-specific setup stacks multiple tailwinds (model lean +
// soft matchup + scoring game + priced edge), ranked by the composite score, with reasons.
export const getNFLPrimeSpots = (season, week, market = '', limit = 12) =>
  tryGet(`/nfl/prime-spots?season=${season}&week=${week}${market ? `&market=${encodeURIComponent(market)}` : ''}&limit=${limit}`,
         { spots: [] })

// Yards + TD combo — P(over a yards line AND scores a TD), the correlated joint. curve=1
// returns the joint across a range of lines so a slider reads it live. Null on error.
export const getNFLCombo = (gsis, yardsMarket, season, week, { curve = 1, line = 0 } = {}) =>
  tryGet(`/nfl/combo?gsis=${encodeURIComponent(gsis)}&yards_market=${yardsMarket}&season=${season}&week=${week}&curve=${curve ? 1 : 0}${line ? `&yards_line=${line}` : ''}`, null)

// Red Zone Power Spots — players with the best anytime-TD setups this week
// (opportunity × opponent RZ defense × implied points). Safe empty default.
export const getNFLRedZoneSpots = (season, week, limit = 12) =>
  tryGet(`/nfl/red-zone-spots?season=${season}&week=${week}&limit=${limit}`, { spots: [], count: 0, rz_data: false })

// Red Zone Power Spots track record — frozen weekly boards graded vs who scored:
// hit rate, ROI, the +EV (VALUE) subset, calibration, recent graded spots.
export const getNFLRedZoneRecord = (season) =>
  tryGet(`/nfl/red-zone-record?season=${season}`, { overall: null, value: null, calibration: [], recent: [] })

export const getNFLFantasy = (season, week, scoring, pos, limit) =>
  tryGet(`/nfl/fantasy?season=${season}&week=${week}&scoring=${scoring || 'ppr'}&pos=${pos || 'FLEX'}${limit ? `&limit=${limit}` : ''}`, { rows: [] })

// Value-based draft board — ranks by season points over replacement for the given
// league (teams + starters), so each player carries a target round + tier cliff.
export const getNFLDraft = (season, opts = {}) => {
  const q = new URLSearchParams({ season,
    teams: opts.teams || 12, scoring: opts.scoring || 'ppr',
    ...(opts.superflex ? { superflex: opts.superflex } : {}) })
  return tryGet(`/nfl/draft?${q.toString()}`, { rows: [], replacement: {}, teams: opts.teams || 12 })
}

export const getNFLOptimizer = (ids, season, week, scoring) =>
  tryGet(`/nfl/fantasy/optimizer?ids=${encodeURIComponent((ids || []).join(','))}&season=${season}&week=${week}&scoring=${scoring || 'ppr'}`,
         { mode: 'slate', lineup: [], bench: [], total: 0 })

export const getNFLWaivers = (season, week, scoring, exclude) =>
  tryGet(`/nfl/fantasy/waivers?season=${season}&week=${week}&scoring=${scoring || 'ppr'}&exclude=${encodeURIComponent((exclude || []).join(','))}`,
         { rows: [] })

export const getNFLFantasyMatchup = (me, opp, season, week, scoring) =>
  tryGet(`/nfl/fantasy/matchup?me=${encodeURIComponent((me || []).join(','))}&opp=${encodeURIComponent((opp || []).join(','))}&season=${season}&week=${week}&scoring=${scoring || 'ppr'}`,
         { me: null, opp: null, me_win_prob: null, margin: 0 })

export const getNFLCascadeTeams = (season, week, scoring) =>
  tryGet(`/nfl/cascade/teams?season=${season}&week=${week}${scoring ? `&scoring=${scoring}` : ''}`, { teams: [], live: [] })

export const getNFLCascade = (out, season, week, scoring) =>
  tryGet(`/nfl/cascade?out=${encodeURIComponent(out)}&season=${season}&week=${week}${scoring ? `&scoring=${scoring}` : ''}`,
         { out: null, vacated: {}, beneficiaries: [], angle: '' })

// Legs are 'gsis_id:prop_type' tokens (or bare gsis_id → anytime_td).
export const getNFLParlay = (tokens, season, week, bookOdds = 0) =>
  tryGet(`/nfl/parlay?ids=${encodeURIComponent(tokens.join(','))}&season=${season}&week=${week}${bookOdds ? `&book_odds=${bookOdds}` : ''}`, {})

// UI market label → db prop_type (mirrors the backend PROP_LABEL reverse map).
export const MARKET_KEY = {
  'Receptions': 'receptions', 'Anytime TD': 'anytime_td', 'Pass TDs': 'pass_tds',
  'Rec Yds': 'rec_yards', 'Rush Yds': 'rush_yards', 'Pass Yds': 'pass_yards',
}

export const getNFLPlayerMatchup = (gsisId, opp, pos, season, week, bet = {}) => {
  const q = new URLSearchParams({ gsis_id: gsisId, opp, pos, season, week })
  if (bet.market) q.set('market', bet.market)
  if (bet.line != null) q.set('line', bet.line)
  if (bet.prob != null) q.set('prob', bet.prob)
  if (bet.edge != null) q.set('edge', bet.edge)
  return tryGet(`/nfl/player-matchup?${q.toString()}`, {})
}

// Coverage profile: zone/man per-route splits (completed seasons), first-read share (this season),
// the opponent's coverage mix and a plain-English fit.
export const getNFLCoverageProfile = (gsisId, opp, season) =>
  tryGet(`/nfl/coverage-profile?gsis_id=${encodeURIComponent(gsisId)}&opp=${encodeURIComponent(opp || '')}&season=${season}`, {})

export const getNFLPlayerSplits = (gsisId, opp, pos, season) =>
  tryGet(`/nfl/player-splits?gsis_id=${encodeURIComponent(gsisId)}&opp=${encodeURIComponent(opp || '')}&pos=${pos}&season=${season}`, {})

export const getNFLPlayerIntel = (gsisId, market, line, season) =>
  tryGet(`/nfl/player-intel?gsis_id=${encodeURIComponent(gsisId)}&market=${encodeURIComponent(market || 'Receptions')}&line=${line || 0}&season=${season}`, { n: 0 })

// Name search; a team name ('Bills', 'Buffalo', 'BUF') returns that team's players this week.
export const searchNFLPlayers = (q, season, week) =>
  tryGet(`/nfl/players?search=${encodeURIComponent(q)}${season ? `&season=${season}` : ''}${week ? `&week=${week}` : ''}`, { players: [] })

// Every market a player is projected in this week (the cross-market view).
export const getNFLPlayerProps = (gsisId, season, week) =>
  tryGet(`/nfl/player-props?gsis_id=${encodeURIComponent(gsisId)}&season=${season}&week=${week}`, { markets: [] })

export const getNFLPlayer = (gsisId) =>
  tryGet(`/nfl/players/${gsisId}`, { logs: [] })

export const getNFLGrid = (pos = 'WR', season, week) =>
  tryGet(`/nfl/grid?pos=${pos}&season=${season}&week=${week}`, { pos, rows: [] })

export const getNFLMatchups = (season, week) =>
  tryGet(`/nfl/matchups?season=${season}&week=${week}`, { rows: [] })

// Football-only Matchup Matrix — every weekly game graded 0-100 + A+..F per team.
// No gambling outputs (no spreads/picks/parlays) — pure on-field matchup analysis.
export const getNFLMatchupMatrix = (season, week) =>
  tryGet(`/nfl/matchup/matrix?season=${season}&week=${week}`,
         { season, week, prior_weight: null, games: [], count: 0 })

// Top N plays per game (default 5), ranked by the model's hit probability — for the
// "Top Plays" board and one-tap copy/export.
export const getNFLTopPlays = (season, week, n = 5) =>
  tryGet(`/nfl/top-plays?season=${season}&week=${week}&n=${n}`, { season, week, n, games: [], count: 0 })

// Ask BRKR — natural-language research over the board. Returns a one-line answer + matching
// plays (or a record/signals summary). Research only — never a parlay or a bet instruction.
// ai=1 → Claude phrases the answer conversationally (grounded in the same data); the backend
// falls back to a templated answer if the AI key is unset or the call fails.
export const askNFL = (q, season, week, ai = 1) =>
  tryGet(`/nfl/ask?q=${encodeURIComponent(q || '')}&season=${season}&week=${week}&ai=${ai ? 1 : 0}`,
         { query: q, type: 'empty', answer: '', plays: [] })

export const getNFLMatchupGame = (gameId, season, week) =>
  tryGet(`/nfl/matchup/game?game_id=${encodeURIComponent(gameId)}&season=${season}&week=${week}`, {})

export const getNFLScorers = (season, week, n = 6) =>
  tryGet(`/nfl/scorers?season=${season}&week=${week}&n=${n}`, { season, week, games: [] })

// Matchup cheatsheet — the week's matchups as sortable category tables (Passing /
// Rushing / Receiving / Red Zone / Alignment), each row an offense vs its opponent
// with the matchup grade + the opponent defense's softness rank. Football research only.
export const getNFLCheatsheet = (season, week) =>
  tryGet(`/nfl/cheatsheet?season=${season}&week=${week}`,
         { season, week, games: 0, categories: [] })

export const getNFLWeeklyRecord = (season) =>
  tryGet(`/nfl/weekly-record?season=${season}`, { season, weeks: [], recent_form: null })

// Week-over-week breakdown of the top-N conviction plays: each settled week's record + the
// full play list (player, market, line, shown %, tier, hit/miss). Newest week first.
export const getNFLTop20Weekly = (season, n = 20) =>
  tryGet(`/nfl/top20-weekly?season=${season}&n=${n}`, { season, n, weeks: [], overall: null })

export const getNFLDefense = (season, week) =>
  tryGet(`/nfl/defense?season=${season}&week=${week}`, { rows: [] })

// Proof layer — our graded record when we attacked a soft defense (overall + by market).
export const getNFLAttackProof = (season) =>
  tryGet(`/nfl/attack-proof?season=${season}`, { overall: null, by_market: {} })

// Blowout Spots — games with blowout potential (big favorite) and the favored team's RB
// room, flagging the backup RB when he has garbage-time value. Football research only.
export const getNFLBlowoutSpots = (season, week) =>
  tryGet(`/nfl/blowout-spots?season=${season}&week=${week}`, { spots: [] })

// Matchup Sheets — the softest defenses per position (TDs + yards) with the players
// facing them this week and the model's read. One fetch powers the whole view.
export const getNFLMatchupSheets = (season, week) =>
  tryGet(`/nfl/matchup-sheets?season=${season}&week=${week}`, { td: [], yards: [] })

// DUE board — players the model still projects to score (prob >= min_prob) who haven't
// scored in their last min_drought+ games, with their recent touches shown. Positive-TD-
// regression value, not the gambler's fallacy. Research only.
export const getNFLDueTds = (season, week, n = 12, { minTouches, minProb } = {}) => {
  const q = [`season=${season}`, `week=${week}`, `n=${n}`]
  if (minTouches != null) q.push(`min_touches=${minTouches}`)
  if (minProb != null) q.push(`min_prob=${minProb}`)
  return tryGet(`/nfl/due-tds?${q.join('&')}`, { due: [] })
}

// This week's card — surfaced plays with live HIT / MISS / PENDING status + running record.
// Grades land as each game finalizes (settlement is post-game, not live in-game).
export const getNFLResults = (season, week, scope = 'featured') =>
  tryGet(`/nfl/results?season=${season}&week=${week}&scope=${scope}`,
         { season, week, scope, record: { wins: 0, losses: 0, pushes: 0, pending: 0, hit_rate: null }, plays: [] })

export const getNFLScorecard = (season, week = 0) =>
  tryGet(`/nfl/scorecard?season=${season}&week=${week}`,
         { overall:{plays:0,wins:0,losses:0,hit_rate:0}, by_prop:{}, top20:{plays:0,hit_rate:0} })

// FUN narrative angles for the week — birthdays landing on game day, historical storylines,
// and "faces his former team" revenge spots. Engagement only — NO probabilities, never edge.
export const getNFLAngles = (season, week) =>
  tryGet(`/nfl/angles?season=${season}&week=${week}`,
         { season, week, games: [], disclaimer: '' })

// The NAMED, graded picks behind the advertised record — the "receipts". Every recommended
// play, who hit and who didn't, grouped by week + market. Reconciles with the scorecard.
// market='' = all markets; week=0 = whole season.
export const getNFLRecordPlays = (season, market = '', week = 0) =>
  tryGet(`/nfl/record-plays?season=${season}&market=${market}&week=${week}`,
         { season, market: market || 'all', week: week || 'season',
           record: { plays: 0, wins: 0, losses: 0, hit_rate: null }, by_week: {}, by_market: {}, plays: [] })

// Sharpest Spots — the slate's best-VALUE plays, ranked by edge + in-week CLV (steam) +
// market beatability (not raw hit prob). The "where's the real edge tonight" board.
export const getNFLSharpestSpots = (season, week, n = 12) =>
  tryGet(`/nfl/sharpest-spots?season=${season}&week=${week}&n=${n}`, { season, week, spots: [], count: 0 })

// Market edge — which markets actually beat the book this season (SHARP/NEUTRAL/EFFICIENT),
// so the board can steer users toward the beatable ones. Empty until markets settle enough.
export const getNFLMarketEdge = (season) =>
  tryGet(`/nfl/market-edge?season=${season}`, { season, markets: [], graded: 0, sharpest: null })

// Game-outcome record — ATS / totals / moneyline hit rates + win-prob calibration.
export const getNFLGameRecord = (season, week = 0) =>
  tryGet(`/nfl/game-record?season=${season}&week=${week}`,
         { games_graded:0, ats:{plays:0,hit_rate:null}, ou:{plays:0,hit_rate:null}, ml:{plays:0,hit_rate:null}, win_prob:{brier:null,buckets:[]} })

// Realized fantasy-point accuracy (projected vs actual) over the backtest seasons.
export const getNFLFantasyAccuracy = (scoring) =>
  tryGet(`/nfl/fantasy/accuracy${scoring ? `?scoring=${scoring}` : ''}`,
         { overall:{n:0}, by_pos:{}, starters:{n:0}, tiers:[] })

export const getNFLCalibration = (season, prop = '') =>
  tryGet(`/nfl/calibration?season=${season}&prop=${prop}`,
         { overall:{plays:0,hit_rate:0}, brier:null, buckets:[] })

export const getNFLTrending = (season, week) =>
  tryGet(`/nfl/trending?season=${season}&week=${week}`, { risers: [], fallers: [] })

// Coaching / scheme watch — offseason coordinator changes + scheme shifts detected from
// play data. A research nugget: a new OC/DC means last year's tendencies may not hold.
export const getNFLCoaching = (season) =>
  tryGet(`/nfl/coaching?season=${season}`,
         { coordinator_changes: { off: [], def: [] }, detected_from_play_data: { off: [], def: [] }, detail: [] })

// Simulation check (Pro) — replays every game on the slate with correlated variance and
// compares the board's independent props to the joint outcome. Surfaces the props where
// the board may be over/under-confident. Compute-heavy → call on demand, not on load.
export const getNFLSim = (season, week) =>
  tryGet(`/nfl/sim?season=${season}&week=${week}`,
         { season, week, games: 0, props: 0, by_market: {}, top_divergences: [] })

export const getNFLConsensus = (season, week) =>
  tryGet(`/nfl/consensus?season=${season}&week=${week}`, { rows: [] })

// Season-long futures (player predictions), powered by the season projector.
export const FUTURES_STATS = [
  { key: 'pass_yards', label: 'Passing Yds' },
  { key: 'pass_tds',   label: 'Passing TDs' },
  { key: 'rush_yards', label: 'Rushing Yds' },
  { key: 'rush_tds',   label: 'Rushing TDs' },
  { key: 'rec_yards',  label: 'Receiving Yds' },
  { key: 'receptions', label: 'Receptions' },
  { key: 'rec_tds',    label: 'Receiving TDs' },
]
export const getFutures = (season, stat = 'pass_yards') =>
  tryGet(`/nfl/futures?season=${season}&stat=${stat}`, { stat, leaders: [] })

export const getTeamFutures = (season) =>
  tryGet(`/nfl/team-futures?season=${season}`, { teams: [] })

// Full-season projections — per-player stat outlooks (per_game / total / floor / ceiling),
// filterable by position + stat. The position-filterable, player-centric companion to the
// per-stat Futures leaders.
export const getNFLSeason = (season, position = '', stat = '') =>
  tryGet(`/nfl/season?season=${season}${position ? `&position=${position}` : ''}${stat ? `&stat=${stat}` : ''}`,
         { season, position, stat, players: [] })

export const getNFLStatus = (season, week) =>
  tryGet(`/nfl/status?season=${season}${week ? `&week=${week}` : ''}`, { projections: { weeks: [], latest_week: null } })

export const getNFLValidation = (season) =>
  tryGet(`/nfl/validation?season=${season}`, { markets: [], overall: {} })

// Owner code for ops — the master/owner code that authorizes pipeline jobs. Stored
// separately from the login code, because the owner may browse the board on a beta
// code that can't run admin jobs. Entered once in the Ops panel, then reused.
export const getNFLOps = () => { try { return localStorage.getItem('brkr_ops') || '' } catch (_) { return '' } }
export const setNFLOps = (c) => { try { localStorage.setItem('brkr_ops', (c || '').trim()) } catch (_) {} }

// Owner ops — trigger a pipeline job. Uses the saved owner code if set, else the
// code the owner is logged in with. /nfl/admin bypasses the access gate and
// authorizes on ?key=; the backend accepts the master access code and rejects beta
// codes, so this is a safe no-op for non-owners.
export async function runNFLJob(job, season = 2026) {
  const code = getNFLOps() || getNFLAccess()
  try {
    const r = await fetch(`${BASE}/nfl/admin/run?job=${job}&season=${season}&key=${encodeURIComponent(code)}`,
      { headers: _hdrs() })
    const body = await r.json().catch(() => ({}))
    return { ok: r.ok, status: r.status, body }
  } catch (e) {
    return { ok: false, status: 0, body: { detail: String(e) } }
  }
}

// Owner: scratch a player (mark OUT) for a week — suspensions, late scratches, cuts the
// injury feed never reports. remove=true undoes it. Owner-authed like runNFLJob; the
// backend only clears its own manual rows, never a real injury-feed row.
export async function scratchNFLPlayer(name, { reason = 'Scratched', remove = false, season = 2026, week = 1 } = {}) {
  const code = getNFLOps() || getNFLAccess()
  const q = new URLSearchParams({ name, reason, season, week, key: code })
  if (remove) q.set('remove', '1')
  try {
    const r = await fetch(`${BASE}/nfl/admin/mark-out?${q.toString()}`, { method: 'POST', headers: _hdrs() })
    const body = await r.json().catch(() => ({}))
    return { ok: r.ok, status: r.status, body }
  } catch (e) {
    return { ok: false, status: 0, body: { detail: String(e) } }
  }
}

// Owner board audit — full player-by-player anomaly scan. Owner-authed like runNFLJob.
export async function getNFLAudit(season = 2026, week = 1) {
  const code = getNFLOps() || getNFLAccess()
  try {
    const r = await fetch(`${BASE}/nfl/admin/audit?season=${season}&week=${week}&key=${encodeURIComponent(code)}`,
      { headers: _hdrs() })
    const body = await r.json().catch(() => ({}))
    return { ok: r.ok, status: r.status, body }
  } catch (e) {
    return { ok: false, status: 0, body: { detail: String(e) } }
  }
}

// ── Referrals (owner) — tally, reward payouts, and minting named codes. Owner-authed
// like the other ops calls (?key= with the ops/access code; the backend accepts the master).
export async function getNFLReferralsAdmin() {
  const code = getNFLOps() || getNFLAccess()
  try {
    const r = await fetch(`${BASE}/nfl/admin/referrals?format=json&key=${encodeURIComponent(code)}`)
    const b = await r.json().catch(() => ({}))
    return r.ok ? b : { _error: r.status, referrers: [] }
  } catch (e) { return { _error: String(e), referrers: [] } }
}
export async function nflReferralPayout(refCode, amount, note = '') {
  const code = getNFLOps() || getNFLAccess()
  try {
    const r = await fetch(`${BASE}/nfl/admin/referral-payout?key=${encodeURIComponent(code)}&code=${encodeURIComponent(refCode)}&amount=${encodeURIComponent(amount)}&note=${encodeURIComponent(note)}`)
    const b = await r.json().catch(() => ({}))
    return r.ok ? b : { ok: false, detail: b.detail || r.status }
  } catch (e) { return { ok: false, detail: String(e) } }
}
export async function nflAttributeReferral(refCode, name, qualified = true, note = '') {
  const code = getNFLOps() || getNFLAccess()
  try {
    const r = await fetch(`${BASE}/nfl/admin/referral-attribute?key=${encodeURIComponent(code)}&code=${encodeURIComponent(refCode)}&name=${encodeURIComponent(name)}&qualified=${qualified ? 1 : 0}&note=${encodeURIComponent(note)}`)
    const b = await r.json().catch(() => ({}))
    return r.ok ? b : { ok: false, detail: b.detail || r.status }
  } catch (e) { return { ok: false, detail: String(e) } }
}
export async function nflMakeRefCode(refCode, label = '') {
  const code = getNFLOps() || getNFLAccess()
  try {
    const r = await fetch(`${BASE}/nfl/admin/referral-code?key=${encodeURIComponent(code)}&code=${encodeURIComponent(refCode)}&label=${encodeURIComponent(label)}`)
    const b = await r.json().catch(() => ({}))
    return r.ok ? b : { ok: false, detail: b.detail || r.status }
  } catch (e) { return { ok: false, detail: String(e) } }
}

export const getNFLTierRecord = (season, week = 0) =>
  tryGet(`/nfl/tier-record?season=${season}&week=${week}`, { tiers: [], overall: {} })

// Pick'em / DFS +EV board — each PrizePicks/Underdog/Pick6 line vs the model's prob at that line.
export const getNFLPickemBoard = (season = 2026, week = 1) =>
  tryGet(`/nfl/pickem-board?season=${season}&week=${week}`, { plays: [], count: 0, dfs_enabled: false })

// Market Pulse — line movement (open → now) with a with/against-the-model tag.
export const getNFLMarketPulse = (season = 2026, week = 1) =>
  tryGet(`/nfl/market-pulse?season=${season}&week=${week}`, { movers: [], count: 0 })

// Game-market board — moneyline / spread / total per game + line movement + sharp-vs-square.
export const getNFLMarket = (season = 2026, week = 1) =>
  tryGet(`/nfl/market?season=${season}&week=${week}`, { games: [] })

// Affiliate book links (book_key → tracked URL); empty until set in Railway.
export const getNFLBookLinks = () => tryGet('/nfl/book-links', {})

// PUBLIC track-record page (ungated) — settled record + CLV + tier breakdown +
// weekly trend + calibration in one shot. Powers the shareable /proof page a cold
// visitor sees before the paywall. No code needed (the endpoint is gate-exempt).
export const getNFLProof = (season) =>
  tryGet(`/nfl/proof?season=${season}`,
         { season, settled: 0, overall: null, realized: null, top20: null,
           by_prop: [], tiers: [], weeks: [], recent_form: null,
           calibration: { brier: null, buckets: [] } })

// ── Web Push (notify me when results are in) ─────────────────────────────────
// Standard VAPID Web Push against the app's existing service worker (/sw.js). Works
// on desktop/Android browsers and on iOS 16.4+ ONLY when the app is added to the
// home screen (installed PWA) — pushIsSupported reflects that. Dormant until the
// backend has VAPID keys (getPushVapid → enabled:false), so the UI hides the toggle.

export const pushIsSupported = () =>
  typeof navigator !== 'undefined' && 'serviceWorker' in navigator &&
  typeof window !== 'undefined' && 'PushManager' in window && 'Notification' in window

export const getPushVapid = () =>
  tryGet('/nfl/push/vapid', { enabled: false, public_key: '' })

function _urlB64ToUint8(b64) {
  const pad = '='.repeat((4 - (b64.length % 4)) % 4)
  const raw = atob((b64 + pad).replace(/-/g, '+').replace(/_/g, '/'))
  const out = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i)
  return out
}

// Current opt-in state: is push supported, configured server-side, permission, and is
// this device already subscribed. Never throws.
export async function getPushState() {
  if (!pushIsSupported()) return { supported: false, enabled: false, permission: 'default', subscribed: false }
  const v = await getPushVapid()
  let subscribed = false
  try {
    const reg = await navigator.serviceWorker.ready
    subscribed = !!(await reg.pushManager.getSubscription())
  } catch (_) {}
  return { supported: true, enabled: !!v.enabled, publicKey: v.public_key || '',
           permission: (typeof Notification !== 'undefined' && Notification.permission) || 'default',
           subscribed }
}

// Request permission + subscribe this device. Returns {ok} or {ok:false, reason}.
export async function subscribePush() {
  if (!pushIsSupported()) return { ok: false, reason: 'unsupported' }
  const v = await getPushVapid()
  if (!v.enabled || !v.public_key) return { ok: false, reason: 'not_configured' }
  let perm = Notification.permission
  if (perm === 'default') perm = await Notification.requestPermission()
  if (perm !== 'granted') return { ok: false, reason: 'denied' }
  try {
    const reg = await navigator.serviceWorker.ready
    let sub = await reg.pushManager.getSubscription()
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: _urlB64ToUint8(v.public_key),
      })
    }
    const r = await fetch(`${BASE}/nfl/push/subscribe`, {
      method: 'POST', headers: { 'content-type': 'application/json', ..._hdrs() },
      body: JSON.stringify({ ...sub.toJSON(), weekly_recap: true }),
    })
    return { ok: r.ok }
  } catch (e) {
    return { ok: false, reason: 'error' }
  }
}

// Unsubscribe this device (best-effort both sides).
export async function unsubscribePush() {
  if (!pushIsSupported()) return { ok: true }
  try {
    const reg = await navigator.serviceWorker.ready
    const sub = await reg.pushManager.getSubscription()
    if (sub) {
      const endpoint = sub.endpoint
      await sub.unsubscribe().catch(() => {})
      await fetch(`${BASE}/nfl/push/unsubscribe`, {
        method: 'POST', headers: { 'content-type': 'application/json', ..._hdrs() },
        body: JSON.stringify({ endpoint }),
      }).catch(() => {})
    }
    return { ok: true }
  } catch (_) { return { ok: true } }
}

// ── Custom notification preferences ───────────────────────────────────────────
// This device's push endpoint (the key prefs are stored under). Null when not subscribed.
export async function getPushEndpoint() {
  if (!pushIsSupported()) return null
  try {
    const reg = await navigator.serviceWorker.ready
    const sub = await reg.pushManager.getSubscription()
    return sub?.endpoint || null
  } catch (_) { return null }
}

export const getNFLNotifyPrefs = (endpoint) =>
  tryGet(`/nfl/push/prefs?endpoint=${encodeURIComponent(endpoint || '')}`, { prefs: null })

export async function setNFLNotifyPrefs(endpoint, prefs) {
  try {
    const r = await fetch(`${BASE}/nfl/push/prefs`, {
      method: 'POST', headers: { 'content-type': 'application/json', ..._hdrs() },
      body: JSON.stringify({ endpoint, prefs }),
    })
    return r.ok ? await r.json() : null
  } catch (_) { return null }
}

// ── Access gate (soft-launch on codes) ───────────────────────────────────────
// The NFL app sits behind the same access-code gate as MLB (localStorage
// 'brkr_access'). Un-coded visitors must be shown a lock screen — NOT silently
// served sample data (which tryGet would do on a 401) — so NFLApp probes the gate
// against a GATED endpoint before rendering.
export const getNFLAccess = () => { try { return localStorage.getItem('brkr_access') || '' } catch (_) { return '' } }
export const setNFLAccess = (c) => { try { localStorage.setItem('brkr_access', (c || '').trim()) } catch (_) {} }

// ── Free tier (email signup → capped free board) ──────────────────────────────
// The token from a free signup is stored locally and sent to /nfl/free/board. No access
// code involved — this is the no-code growth path.
export const getNFLFreeToken = () => { try { return localStorage.getItem('brkr_free') || '' } catch (_) { return '' } }
export const setNFLFreeToken = (t) => { try { localStorage.setItem('brkr_free', (t || '').trim()) } catch (_) {} }
// The referral tag this visitor arrived with (?ref=…), captured in App.jsx and stored so it
// rides through both the free signup and the paid checkout.
export const getNFLRef = () => { try { return localStorage.getItem('brkr_ref') || '' } catch (_) { return '' } }
// Persist a referral code a user TYPED in (not just arrived with via ?ref=), so it rides the
// same localStorage slot the link uses — through both the free signup and the paid checkout.
export const setNFLRef = (code) => { try { const c = (code || '').trim(); if (c) localStorage.setItem('brkr_ref', c) } catch (_) {} }
export async function nflFreeSignup(email, ref) {
  const useRef = (ref && ref.trim()) || getNFLRef()
  if (ref && ref.trim()) setNFLRef(ref)
  const r = await fetch(`${BASE}/nfl/free/signup`, {
    method: 'POST', headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ email, ref: useRef }) })
  const d = await r.json().catch(() => ({}))
  if (!r.ok || !d.token) throw new Error(d.detail || 'Could not sign up. Try again.')
  setNFLFreeToken(d.token)
  return d
}
export const getNFLReferral = () =>
  tryGet(`/nfl/free/referral?token=${encodeURIComponent(getNFLFreeToken())}`, { ref_code: '', referred: 0 })
export const getNFLFreeBoard = (season = 2026) =>
  tryGet(`/nfl/free/board?token=${encodeURIComponent(getNFLFreeToken())}&season=${season}`,
    { plays: [], free: true })

// Probe a gated endpoint (team-futures — NOT status, which is exempt). 200 → open
// (valid code, or no gate configured); 401 → locked (a code is required). Network
// or other errors resolve to 'open' so a transient blip never traps a valid user
// behind the lock screen — the tabs handle their own load errors.
// ── Purchase (Stripe checkout for NFL) ───────────────────────────────────────
// /billing/* is open (a buyer has no code yet). These power the "Get NFL access"
// button on the lock screen and the post-checkout claim.
export async function getBillingStatus() {
  try { const r = await fetch(`${BASE}/billing/status`); return r.ok ? await r.json() : {} }
  catch (_) { return {} }
}
export async function startNFLCheckout(plan = 'single:nfl', email = null) {
  let ref = ''
  try { ref = localStorage.getItem('brkr_ref') || '' } catch (_) {}
  const r = await fetch(`${BASE}/billing/checkout`, {
    method: 'POST', headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ plan, sport: 'nfl', email, ref }) })
  const d = await r.json().catch(() => ({}))
  if (!r.ok || !d.url) throw new Error(d.detail || 'Could not start checkout')
  return d.url
}
export async function claimCheckout(sessionId) {
  try {
    const r = await fetch(`${BASE}/billing/claim`, {
      method: 'POST', headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ session_id: sessionId }) })
    return r.ok ? await r.json() : null      // { code, plan, status }
  } catch (_) { return null }
}

export async function checkNFLAccess(season = 2026) {
  try {
    const r = await fetch(`${BASE}/nfl/team-futures?season=${season}`, { headers: _hdrs() })
    if (r.status === 401) return 'locked'      // no / invalid code
    if (r.status === 402) return 'upgrade'     // valid code, but doesn't include NFL
    return 'open'
  } catch (_) { return 'open' }
}

// ===========================================================================
// COMMUNITY — identity/handle, the feedback roadmap, logged picks (leaderboard
// + Wall), reactions/comments/moderation. Identity is sent two ways so BOTH a
// paid user (x-access-code via _hdrs) and a free user (?token=) resolve: the
// access-code header plus the free token appended to the query string.
// ===========================================================================

const _tok = () => { try { return localStorage.getItem('brkr_free') || '' } catch (_) { return '' } }
const _withTok = (path) => path + (path.includes('?') ? '&' : '?') + `token=${encodeURIComponent(_tok())}`

// POST/DELETE with JSON body + both identity channels. Returns parsed JSON or throws on !ok.
async function _send(path, body, method = 'POST') {
  const r = await fetch(`${BASE}${_withTok(path)}`, {
    method, headers: { 'content-type': 'application/json', ..._hdrs() },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  })
  let d = {}
  try { d = await r.json() } catch (_) {}
  if (!r.ok) throw new Error(d.detail || 'Request failed')
  return d
}

// --- identity / handle ---
export const getCommunityMe = () => tryGet(_withTok('/nfl/me'), { signed_in: false, handle: '' })
export const setCommunityHandle = (handle) => _send('/nfl/me/handle', { handle })

// --- community guidelines (the rules the moderation bot enforces) ---
export const getCommunityGuidelines = () => tryGet('/nfl/community/guidelines', { guidelines: [] })

// --- my removal notices (posts the bot took down, with the reason). Marks them seen. ---
export const getMyModerationNotices = () => tryGet(_withTok('/nfl/me/notices'), { notices: [] })

// --- feedback / upvote roadmap ---
export const getNFLFeedback = (status = '', sort = 'votes') =>
  tryGet(_withTok(`/nfl/feedback?status=${encodeURIComponent(status)}&sort=${sort}`),
         { items: [], status_counts: {} })
export const postNFLFeedback = (title, body, category = 'feature') =>
  _send('/nfl/feedback', { title, body, category })
export const voteNFLFeedback = (id) => _send(`/nfl/feedback/${id}/vote`, {})

// --- logged picks + leaderboard ---
export const logNFLPick = (pick) => _send('/nfl/picks', pick)
export const getNFLMyPicks = (season) => tryGet(_withTok(`/nfl/picks/mine?season=${season}`),
  { record: { wins: 0, losses: 0, pushes: 0, win_pct: null, pending: 0 }, picks: [] })
export const deleteNFLPick = (id) => _send(`/nfl/picks/${id}`, undefined, 'DELETE')
export const getNFLCommunityLeaderboard = (season, week = 0, minPicks = 3) =>
  tryGet(`/nfl/leaderboard?season=${season}&week=${week}&min_picks=${minPicks}`, { leaders: [] })

// --- The Wall + reactions / comments / reports ---
export const getNFLWall = (season, week = 0, sort = 'new') =>
  tryGet(_withTok(`/nfl/wall?season=${season}&week=${week}&sort=${sort}`), { posts: [] })
export const reactNFL = (targetType, targetId, kind) =>
  _send('/nfl/react', { target_type: targetType, target_id: targetId, kind })
export const getNFLComments = (targetType, targetId) =>
  tryGet(`/nfl/comments?target_type=${targetType}&target_id=${encodeURIComponent(targetId)}`, { comments: [] })
export const postNFLComment = (targetType, targetId, body) =>
  _send('/nfl/comments', { target_type: targetType, target_id: targetId, body })
export const reportNFL = (targetType, targetId, reason = '') =>
  _send('/nfl/report', { target_type: targetType, target_id: targetId, reason })
